import React, { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    AlertTriangle, ArrowUpRight, Ban, Calculator, CheckCircle2, Copy,
    ExternalLink, FileText, Filter, Loader2, MousePointerClick,
    RefreshCw, Rocket, Send, ShieldAlert, Sparkles, TrendingUp,
    Users, Wallet, X,
} from "lucide-react";
import { toast } from "sonner";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/data/v12_user_nav";


function KpiCard({ icon: Icon, label, value, tone = "primary", testid, hint }) {
    const toneClass = {
        primary: "text-[var(--color-primary)]",
        success: "text-[var(--color-success)]",
        alert: "text-[var(--color-alert)]",
        warning: "text-neutral-200",
        secondary: "text-[var(--color-secondary)]",
    }[tone];
    return (
        <div
            data-testid={testid}
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4"
        >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-2">
                <Icon className={`h-3 w-3 ${toneClass}`} /> {label}
            </div>
            <div className={`text-2xl font-semibold tabular-nums ${toneClass}`}>
                {value}
            </div>
            {hint && <p className="text-[10px] text-text-secondary mt-1 leading-tight">{hint}</p>}
        </div>
    );
}


function SeverityBadge({ severity }) {
    const cls = {
        critical: "text-[var(--color-alert)] bg-[var(--color-alert)]/10 border-[var(--color-alert)]/40",
        high: "text-neutral-200 bg-neutral-800 border-neutral-700",
        medium: "text-[var(--color-secondary)] bg-[var(--color-secondary)]/10 border-[var(--color-secondary)]/40",
        low: "text-text-secondary bg-white/5 border-white/10",
    }[severity] || "text-text-secondary bg-white/5 border-white/10";
    return (
        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${cls}`}>
            {severity}
        </span>
    );
}


function StatusBadge({ status }) {
    const cls = {
        pending_admin_review: "text-neutral-200 bg-neutral-800 border-neutral-700",
        approved_pending_transfer: "text-[var(--color-secondary)] bg-[var(--color-secondary)]/10 border-[var(--color-secondary)]/40",
        rejected: "text-[var(--color-alert)] bg-[var(--color-alert)]/10 border-[var(--color-alert)]/40",
        paid: "text-[var(--color-success)] bg-[var(--color-success)]/10 border-[var(--color-success)]/40",
        pending_review: "text-neutral-200 bg-neutral-800 border-neutral-700",
        approved: "text-[var(--color-primary)] bg-[var(--color-primary)]/10 border-[var(--color-primary)]/40",
    }[status] || "text-text-secondary bg-white/5 border-white/10";
    return (
        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${cls}`}>
            {(status || "").replace(/_/g, " ")}
        </span>
    );
}


function ConfirmModal({ open, title, subtitle, tone, confirmLabel, onConfirm, onCancel, busy, children }) {
    const toneCls = {
        primary: "bg-white hover:bg-white/90 text-black",
        alert: "bg-[var(--color-alert)] hover:bg-[var(--color-alert)]/90 text-white",
        warning: "bg-[#A3A3A3] hover:bg-[#A3A3A3]/90 text-black",
        success: "bg-white hover:bg-white/90 text-black",
    }[tone] || "bg-white text-black";
    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
                        onClick={busy ? undefined : onCancel}
                    />
                    <motion.div
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                    >
                        <div
                            data-testid="aff-v2-confirm-modal"
                            className="pointer-events-auto w-full max-w-lg rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
                        >
                            <div className="flex items-start justify-between px-5 py-4 border-b border-[var(--color-border)]">
                                <div>
                                    <h3 className="text-base font-semibold text-text-main">{title}</h3>
                                    {subtitle && <p className="text-xs text-text-secondary mt-1">{subtitle}</p>}
                                </div>
                                <button onClick={onCancel} disabled={busy} className="text-text-secondary hover:text-text-main disabled:opacity-40">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            <div className="p-5 overflow-y-auto flex-1">{children}</div>
                            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[var(--color-border)] bg-[var(--color-bg)]/40">
                                <button
                                    onClick={onCancel} disabled={busy}
                                    className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 disabled:opacity-40"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={onConfirm} disabled={busy}
                                    data-testid="aff-v2-confirm-ok"
                                    className={`text-xs rounded-full px-5 py-2 font-medium flex items-center gap-2 disabled:opacity-50 ${toneCls}`}
                                >
                                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                                    {confirmLabel}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}


const TABS = [
    { key: "affiliates", label: "Affiliates", icon: Users },
    { key: "fraud", label: "Fraud flags", icon: ShieldAlert },
    { key: "payouts", label: "Payouts", icon: Wallet },
    { key: "reports", label: "Weekly report", icon: FileText },
];


export default function AdminAffiliateV2() {
    const { user: me } = useAuth();
    const isAdmin = isAdminRole(me?.role);

    const [tab, setTab] = useState("affiliates");
    const [loading, setLoading] = useState(true);

    const [overview, setOverview] = useState({});
    const [affiliates, setAffiliates] = useState([]);
    const [fraudFlags, setFraudFlags] = useState([]);
    const [payouts, setPayouts] = useState([]);
    const [reports, setReports] = useState([]);

    const [confirmState, setConfirmState] = useState(null);
    const [busy, setBusy] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [resolveNotes, setResolveNotes] = useState("");

    const load = useCallback(async () => {
        try {
            const [ov, aff, ff, po, rp] = await Promise.all([
                api.affV2AdminOverview(),
                api.affV2AdminAffiliates(50),
                api.affV2AdminFraudFlags(false, 100),
                api.affV2AdminPayouts({ limit: 100 }),
                api.affV2AdminReports(10),
            ]);
            setOverview(ov || {});
            setAffiliates(aff?.items || []);
            setFraudFlags(ff?.items || []);
            setPayouts(po?.items || []);
            setReports(rp?.items || []);
        } catch (e) {
            if (e.status !== 503) toast.error(e.message || "Failed to load");
        }
    }, []);

    useEffect(() => {
        (async () => {
            setLoading(true);
            await load();
            setLoading(false);
        })();
    }, [load]);

    // ---- actions -----------------------------------------------------------

    const forceMonthly = async () => {
        setBusy(true);
        try {
            const r = await api.affV2AdminForceMonthlyCalc();
            toast.success(`Monthly calc: ${r.created_count} payout(s) · ${r.skipped_below_min.length} skipped (below $${r.min_payout_eur})`);
            await load();
        } catch (e) {
            toast.error(e.message || "Force calc failed");
        } finally {
            setBusy(false);
        }
    };

    const scanFraud = async () => {
        setBusy(true);
        try {
            const r = await api.affV2AdminScanNow(false);
            toast.success(`Fraud scan: ${r.v2_flags_persisted} v2 flag(s) persisted`);
            await load();
        } catch (e) {
            toast.error(e.message || "Scan failed");
        } finally {
            setBusy(false);
        }
    };

    const generateReport = async () => {
        setBusy(true);
        try {
            const r = await api.affV2AdminReportGenerateNow();
            toast.success(r.already_generated
                ? `Report already generated · suggestion ${r.suggestion_id?.slice(0, 8)}`
                : `Report generated · suggestion ${r.suggestion_id?.slice(0, 8)}`);
            await load();
        } catch (e) {
            toast.error(e.message || "Report generation failed");
        } finally {
            setBusy(false);
        }
    };

    const askResolveFlag = (flag, action) => {
        setResolveNotes("");
        setConfirmState({
            kind: "resolve_flag", flag, action,
            title: action === "confirmed_fraud" ? "Confirm fraud?" : "Mark as false positive?",
            subtitle: action === "confirmed_fraud"
                ? "This flag will be marked resolved. Any linked conversion will be rejected."
                : "This flag will be dismissed. Linked conversion stays as-is.",
            tone: action === "confirmed_fraud" ? "alert" : "primary",
            confirmLabel: action === "confirmed_fraud" ? "Confirm fraud" : "False positive",
        });
    };
    const confirmResolveFlag = async () => {
        setBusy(true);
        try {
            await api.affV2AdminResolveFlag(
                confirmState.flag.id, confirmState.action, resolveNotes);
            toast.success("Flag resolved");
            setConfirmState(null);
            await load();
        } catch (e) {
            toast.error(e.message || "Resolve failed");
        } finally { setBusy(false); }
    };

    const askApprovePayout = (payout) => {
        setConfirmState({
            kind: "approve_payout", payout,
            title: `Approve $${payout.amount_eur}?`,
            subtitle: "Mock ledger only. No Stripe Connect call until Fase 17 LIVE key.",
            tone: "success",
            confirmLabel: "Approve",
        });
    };
    const confirmApprovePayout = async () => {
        setBusy(true);
        try {
            const r = await api.affV2AdminApprovePayout(confirmState.payout.id);
            toast.success(`Approved · status=${r.status} (no Stripe call)`);
            setConfirmState(null);
            await load();
        } catch (e) {
            toast.error(e.message || "Approve failed");
        } finally { setBusy(false); }
    };

    const askRejectPayout = (payout) => {
        setRejectReason("");
        setConfirmState({
            kind: "reject_payout", payout,
            title: `Reject $${payout.amount_eur}?`,
            subtitle: "Conversions will be released back to the pool for the next monthly calc.",
            tone: "alert",
            confirmLabel: "Reject",
        });
    };
    const confirmRejectPayout = async () => {
        if (!rejectReason.trim()) { toast.error("Reason required"); return; }
        setBusy(true);
        try {
            await api.affV2AdminRejectPayout(confirmState.payout.id, rejectReason.trim());
            toast.success("Rejected");
            setConfirmState(null);
            await load();
        } catch (e) {
            toast.error(e.message || "Reject failed");
        } finally { setBusy(false); }
    };

    if (!isAdmin) {
        return (
            <div data-testid="aff-v2-forbidden" className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center">
                <h1 className="text-2xl text-text-main">Admin only</h1>
            </div>
        );
    }

    const confirmHandler = () => {
        if (!confirmState) return;
        if (confirmState.kind === "resolve_flag") return confirmResolveFlag();
        if (confirmState.kind === "approve_payout") return confirmApprovePayout();
        if (confirmState.kind === "reject_payout") return confirmRejectPayout();
    };

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar />
            <div className="flex-1 flex flex-col min-w-0">
                <Header />
                <main
                    data-testid="admin-affiliate-v2-dashboard"
                    className="flex-1 p-6 md:p-8 max-w-6xl mx-auto w-full"
                >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 gap-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] mb-1">
                                Phase 16b · Affiliate v2 Admin
                            </div>
                            <h1 className="text-3xl md:text-4xl font-semibold flex items-center gap-3">
                                <TrendingUp className="h-7 w-7 text-[var(--color-primary)]" />
                                Affiliate program dashboard
                            </h1>
                            <p className="text-sm text-text-secondary mt-1 max-w-2xl">
                                Wraps Phase 3 affiliate (30% commission, 30d cookie, $20 min payout).
                                Adds link tracking (GDPR IP hash), conversion attribution, fraud rules,
                                mock payout ledger, weekly report via Email Send Layer.
                                Zero auto-payout · Zero Stripe call until Fase 17.
                            </p>
                        </div>
                        <button
                            onClick={load}
                            data-testid="aff-v2-refresh"
                            className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 flex items-center gap-2"
                        >
                            <RefreshCw className="h-3 w-3" /> Refresh
                        </button>
                    </div>

                    {/* KPI */}
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-6">
                        <KpiCard icon={Users} label="Total affiliates" value={overview.total_affiliates || 0}
                                  tone="primary" testid="aff-v2-kpi-total" />
                        <KpiCard icon={MousePointerClick} label="Active last 7d" value={overview.active_last_7d || 0}
                                  tone="secondary" testid="aff-v2-kpi-active" hint="Clicks in last 7 days" />
                        <KpiCard icon={Wallet} label="Pending payout $" value={`$${overview.total_pending_payout_eur || 0}`}
                                  tone="warning" testid="aff-v2-kpi-pending-eur"
                                  hint={`${overview.pending_payouts_count || 0} pending row(s)`} />
                        <KpiCard icon={ShieldAlert} label="Fraud flags open" value={overview.fraud_flags_open || 0}
                                  tone={overview.fraud_flags_open > 0 ? "alert" : "success"}
                                  testid="aff-v2-kpi-fraud" />
                    </div>

                    {/* Tabs */}
                    <div className="flex flex-wrap gap-2 mb-4 border-b border-[var(--color-border)] pb-1">
                        {TABS.map((t) => {
                            const Icon = t.icon;
                            const active = tab === t.key;
                            return (
                                <button
                                    key={t.key}
                                    onClick={() => setTab(t.key)}
                                    data-testid={`aff-v2-tab-${t.key}`}
                                    className={`text-xs uppercase tracking-widest px-3 py-2 rounded-t-lg flex items-center gap-2 transition-colors ${
                                        active
                                            ? "text-[var(--color-primary)] border-b-2 border-[var(--color-primary)] -mb-[9px]"
                                            : "text-text-secondary hover:text-text-main"
                                    }`}
                                >
                                    <Icon className="h-3 w-3" /> {t.label}
                                </button>
                            );
                        })}
                    </div>

                    {loading ? (
                        <div className="flex justify-center py-16">
                            <Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" />
                        </div>
                    ) : (
                        <>
                            {tab === "affiliates" && (
                                <section
                                    data-testid="aff-v2-affiliates-section"
                                    className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden"
                                >
                                    <div className="px-5 py-4 border-b border-[var(--color-border)] flex items-center justify-between">
                                        <div>
                                            <h2 className="text-base font-semibold">Affiliates (30-day metrics)</h2>
                                            <p className="text-xs text-text-secondary">Sorted by pending commission.</p>
                                        </div>
                                        <span className="text-[10px] text-text-secondary uppercase tracking-widest">
                                            {affiliates.length} shown
                                        </span>
                                    </div>
                                    <div className="p-4 max-h-[60vh] overflow-y-auto">
                                        {affiliates.length === 0 ? (
                                            <p className="text-xs text-text-secondary italic text-center py-8">
                                                No affiliates yet.
                                            </p>
                                        ) : (
                                            <ul className="space-y-2">
                                                {affiliates.map((a) => (
                                                    <li
                                                        key={a.affiliate_id || a.id || a.user_id}
                                                        data-testid={`aff-v2-affiliate-${a.affiliate_id}`}
                                                        className="rounded-xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-4 py-3 grid gap-2 md:grid-cols-[1fr_auto]"
                                                    >
                                                        <div>
                                                            <div className="text-sm font-medium text-text-main truncate">
                                                                {(a.name || a.email || a.affiliate_id || "").slice(0, 60)}
                                                            </div>
                                                            <div className="text-[11px] text-text-secondary font-mono">
                                                                {a.affiliate_id?.slice(0, 12)}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-3 text-[11px]">
                                                            <span className="text-text-secondary">
                                                                {a.stats_30d?.clicks || 0} clicks
                                                            </span>
                                                            <span className="text-text-secondary">
                                                                {a.stats_30d?.conversions || 0} conv
                                                            </span>
                                                            <span className="text-neutral-200 font-semibold">
                                                                ${a.stats_30d?.pending_commission_eur || 0}
                                                            </span>
                                                            <span className="text-[var(--color-success)]">
                                                                ${a.stats_30d?.paid_commission_eur || 0} paid
                                                            </span>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </section>
                            )}

                            {tab === "fraud" && (
                                <section
                                    data-testid="aff-v2-fraud-section"
                                    className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden"
                                >
                                    <div className="px-5 py-4 border-b border-[var(--color-border)] flex items-center justify-between">
                                        <div>
                                            <h2 className="text-base font-semibold">Fraud flags · pending review</h2>
                                            <p className="text-xs text-text-secondary">Never auto-block — admin decides.</p>
                                        </div>
                                        <button
                                            onClick={scanFraud}
                                            disabled={busy}
                                            data-testid="aff-v2-scan-fraud"
                                            className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-3 py-1.5 flex items-center gap-1 disabled:opacity-40"
                                        >
                                            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldAlert className="h-3 w-3" />}
                                            Scan now
                                        </button>
                                    </div>
                                    <div className="p-4 max-h-[60vh] overflow-y-auto space-y-2">
                                        {fraudFlags.length === 0 ? (
                                            <p className="text-xs text-text-secondary italic text-center py-8">
                                                No open fraud flags.
                                            </p>
                                        ) : (
                                            fraudFlags.map((f) => (
                                                <div
                                                    key={f.id}
                                                    data-testid={`aff-v2-flag-${f.id}`}
                                                    className="rounded-xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-3"
                                                >
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="min-w-0">
                                                            <div className="flex flex-wrap items-center gap-2 mb-1">
                                                                <SeverityBadge severity={f.severity} />
                                                                <span className="text-[10px] uppercase tracking-widest text-text-secondary font-mono">
                                                                    {f.rule_name}
                                                                </span>
                                                            </div>
                                                            <div className="text-[11px] text-text-secondary font-mono truncate">
                                                                affiliate: {f.affiliate_id?.slice(0, 12)} · {(f.flagged_at || "").slice(0, 19).replace("T", " ")}
                                                            </div>
                                                            <details className="text-[11px] text-text-secondary mt-1">
                                                                <summary className="cursor-pointer text-[var(--color-primary)]">
                                                                    Evidence
                                                                </summary>
                                                                <pre className="mt-1 max-h-40 overflow-auto font-mono text-[10px] bg-[var(--color-bg)] rounded p-2">
                                                                    {JSON.stringify(f.evidence, null, 2)}
                                                                </pre>
                                                            </details>
                                                        </div>
                                                        <div className="flex flex-col gap-1 shrink-0">
                                                            <button
                                                                onClick={() => askResolveFlag(f, "confirmed_fraud")}
                                                                data-testid={`aff-v2-flag-confirm-${f.id}`}
                                                                className="text-[10px] rounded-full border border-[var(--color-alert)]/40 text-[var(--color-alert)] px-3 py-1 hover:bg-[var(--color-alert)]/10"
                                                            >
                                                                Confirm fraud
                                                            </button>
                                                            <button
                                                                onClick={() => askResolveFlag(f, "false_positive")}
                                                                data-testid={`aff-v2-flag-dismiss-${f.id}`}
                                                                className="text-[10px] rounded-full border border-[var(--color-border)] text-text-secondary px-3 py-1 hover:text-text-main"
                                                            >
                                                                False positive
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </section>
                            )}

                            {tab === "payouts" && (
                                <section
                                    data-testid="aff-v2-payouts-section"
                                    className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden"
                                >
                                    <div className="px-5 py-4 border-b border-[var(--color-border)] flex items-center justify-between">
                                        <div>
                                            <h2 className="text-base font-semibold">Payouts ledger (mock)</h2>
                                            <p className="text-xs text-text-secondary">
                                                No Stripe call. Fase 17 will add real transfers.
                                            </p>
                                        </div>
                                        <button
                                            onClick={forceMonthly}
                                            disabled={busy}
                                            data-testid="aff-v2-force-monthly"
                                            className="text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-1.5 flex items-center gap-1 disabled:opacity-50"
                                        >
                                            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Calculator className="h-3 w-3" />}
                                            Force monthly calc
                                        </button>
                                    </div>
                                    <div className="p-4 max-h-[60vh] overflow-y-auto space-y-2">
                                        {payouts.length === 0 ? (
                                            <p className="text-xs text-text-secondary italic text-center py-8">
                                                No payouts yet. Click "Force monthly calc" to aggregate.
                                            </p>
                                        ) : (
                                            payouts.map((p) => (
                                                <div
                                                    key={p.id}
                                                    data-testid={`aff-v2-payout-${p.id}`}
                                                    className="rounded-xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-3 flex items-start justify-between gap-3"
                                                >
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <StatusBadge status={p.status} />
                                                            <span className="text-[10px] uppercase tracking-widest text-text-secondary">
                                                                {p.payout_month}
                                                            </span>
                                                        </div>
                                                        <div className="text-lg font-semibold text-[var(--color-primary)]">
                                                            ${p.amount_eur}
                                                        </div>
                                                        <div className="text-[11px] text-text-secondary font-mono truncate">
                                                            aff {p.affiliate_id?.slice(0, 12)} · {(p.conversion_ids || []).length} conv
                                                        </div>
                                                        {p.rejected_reason && (
                                                            <div className="text-[11px] text-[var(--color-alert)] mt-1">
                                                                Reason: {p.rejected_reason}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-col gap-1 shrink-0">
                                                        {p.status === "pending_admin_review" && (
                                                            <>
                                                                <button
                                                                    onClick={() => askApprovePayout(p)}
                                                                    data-testid={`aff-v2-approve-${p.id}`}
                                                                    className="text-[10px] rounded-full bg-white text-black px-3 py-1 hover:bg-white/90"
                                                                >
                                                                    Approve
                                                                </button>
                                                                <button
                                                                    onClick={() => askRejectPayout(p)}
                                                                    data-testid={`aff-v2-reject-${p.id}`}
                                                                    className="text-[10px] rounded-full border border-[var(--color-alert)]/40 text-[var(--color-alert)] px-3 py-1 hover:bg-[var(--color-alert)]/10"
                                                                >
                                                                    Reject
                                                                </button>
                                                            </>
                                                        )}
                                                        {p.status === "approved_pending_transfer" && (
                                                            <span className="text-[10px] text-text-secondary italic">
                                                                Awaiting Fase 17
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </section>
                            )}

                            {tab === "reports" && (
                                <section
                                    data-testid="aff-v2-reports-section"
                                    className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden"
                                >
                                    <div className="px-5 py-4 border-b border-[var(--color-border)] flex items-center justify-between">
                                        <div>
                                            <h2 className="text-base font-semibold">Weekly report history</h2>
                                            <p className="text-xs text-text-secondary">
                                                Creates a pending email_engine_suggestion — 2-click send via
                                                <a href="/admin/email-send" className="text-[var(--color-primary)] underline mx-1">/admin/email-send</a>.
                                            </p>
                                        </div>
                                        <button
                                            onClick={generateReport}
                                            disabled={busy}
                                            data-testid="aff-v2-report-generate"
                                            className="text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-1.5 flex items-center gap-1 disabled:opacity-50"
                                        >
                                            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                                            Generate now
                                        </button>
                                    </div>
                                    <div className="p-4 max-h-[60vh] overflow-y-auto space-y-2">
                                        {reports.length === 0 ? (
                                            <p className="text-xs text-text-secondary italic text-center py-8">
                                                No reports generated yet.
                                            </p>
                                        ) : (
                                            reports.map((r) => (
                                                <div
                                                    key={r.id}
                                                    data-testid={`aff-v2-report-${r.id}`}
                                                    className="rounded-xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-3"
                                                >
                                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                                        <div>
                                                            <div className="text-xs text-text-secondary">
                                                                Week <span className="font-mono text-text-main">{r.week_key}</span>
                                                            </div>
                                                            <div className="text-[11px] text-text-secondary font-mono">
                                                                {(r.generated_at || "").slice(0, 16).replace("T", " ")} UTC · suggestion {r.suggestion_id?.slice(0, 8)}
                                                            </div>
                                                        </div>
                                                        {r.dispatched ? (
                                                            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[var(--color-success)]/40 text-[var(--color-success)] bg-[var(--color-success)]/10">
                                                                dispatched
                                                            </span>
                                                        ) : (
                                                            <a
                                                                href="/admin/email-send"
                                                                className="text-[10px] uppercase tracking-widest px-3 py-0.5 rounded-full border border-neutral-600 text-neutral-200 bg-neutral-800 hover:bg-neutral-700 flex items-center gap-1"
                                                            >
                                                                Pending · send now <ArrowUpRight className="h-3 w-3" />
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </section>
                            )}
                        </>
                    )}
                </main>
            </div>

            {/* Confirm modal */}
            <ConfirmModal
                open={!!confirmState}
                title={confirmState?.title}
                subtitle={confirmState?.subtitle}
                tone={confirmState?.tone}
                confirmLabel={confirmState?.confirmLabel}
                onConfirm={confirmHandler}
                onCancel={() => setConfirmState(null)}
                busy={busy}
            >
                {confirmState?.kind === "reject_payout" && (
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-text-secondary block mb-1">
                            Reason (required, audited)
                        </label>
                        <textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            rows={3}
                            data-testid="aff-v2-reject-reason"
                            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] text-text-main px-3 py-2 text-sm"
                            placeholder="e.g., Fraud pattern detected in linked conversions"
                        />
                    </div>
                )}
                {confirmState?.kind === "resolve_flag" && (
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-text-secondary block mb-1">
                            Notes (optional)
                        </label>
                        <textarea
                            value={resolveNotes}
                            onChange={(e) => setResolveNotes(e.target.value)}
                            rows={3}
                            data-testid="aff-v2-resolve-notes"
                            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] text-text-main px-3 py-2 text-sm"
                            placeholder="Reviewer notes for audit trail"
                        />
                    </div>
                )}
                {confirmState?.kind === "approve_payout" && (
                    <div className="rounded-lg border border-white/15 bg-white/5 p-3 text-[11px] text-text-secondary">
                        <div className="text-white font-semibold mb-1">Mock ledger only</div>
                        <div>Payout ${confirmState.payout.amount_eur} · month {confirmState.payout.payout_month}</div>
                        <div>{(confirmState.payout.conversion_ids || []).length} conversion(s) bundled</div>
                        <div className="mt-2">No Stripe Connect call. Status will change to <code>approved_pending_transfer</code> and stay there until Fase 17.</div>
                    </div>
                )}
            </ConfirmModal>
        </div>
    );
}
